import React, { useCallback, useEffect, useState } from "react";
import {
    Mic,
    ShoppingCart,
    Star,
    Plus,
    Search,
    Radio,
    Fingerprint,
    KeyRound,
    ShieldCheck,
    Volume2,
    ArrowLeft,
    Globe,
    CheckCircle2,
    Utensils,
    ShoppingBasket,
    Store as StoreIcon,
} from "lucide-react";
import { useClara } from "../contexts/ClaraContext";
import {
    DEFAULT_STORE_RESULTS,
    SAMPLE_STORE_RESULTS,
    SHOPPING_CATEGORIES,
} from "../data/sampleData";
import ClaraListening from "../components/ClaraListening";
import VoiceInput from "../components/VoiceInput";
import useSpeechRecognition from "../hooks/useSpeechRecognition";

const STAGES = {
    MENU: "menu",
    LISTENING: "listening",
    STORE: "store",
    SELECTED: "selected",
    CART: "cart",
};

const VOICE_EXAMPLES = [
    "Order pizza from Domino's",
    "Find Crest toothpaste on Amazon",
    "Get groceries from Instacart",
    "Reorder my usual from Whole Foods",
];

const DEMO_COMMANDS = [
    { id: "amazon", label: "Find Crest toothpaste on Amazon", store: { id: "amazon", name: "Amazon" } },
    { id: "doordash", label: "Order from Domino's on DoorDash", store: { id: "doordash", name: "DoorDash" } },
    { id: "instacart", label: "Get groceries from Instacart", store: { id: "instacart", name: "Instacart" } },
];

export const VoiceShopping = () => {
    const { speak } = useClara();
    const sr = useSpeechRecognition();
    const [stage, setStage] = useState(STAGES.MENU);
    const [activeStore, setActiveStore] = useState(null);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    useEffect(() => {
        if (stage === STAGES.MENU) {
            speak(
                "Voice Shopping. Pick a store, or tap Tell Clara Where to Shop to speak.",
            );
        }
    }, [stage, speak]);

    const handleVoiceResult = useCallback(
        (text, err) => {
            if (err) {
                speak(
                    "I didn't catch that. You can tap a store below or use one of the demo commands.",
                );
                return;
            }
            const store = matchStore(text);
            if (store) {
                speak(`Got it. Opening ${store.name}.`);
                setActiveStore(store);
                setStage(STAGES.STORE);
            } else {
                speak(
                    `I heard "${text}". Try saying a store name like Amazon, DoorDash, Instacart, or Target.`,
                );
            }
        },
        [speak],
    );

    const startListening = () => {
        setStage(STAGES.LISTENING);
        speak(
            "I'm listening. Tell me what you want. You can say things like: order pizza from Domino's, find Crest toothpaste on Amazon, or get groceries from Instacart.",
            {
                onEnd: () => {
                    if (sr.supported) sr.start(handleVoiceResult);
                },
            },
        );
    };

    const cancelListening = () => {
        sr.stop();
        speak("Cancelled. Pick a store anytime.");
        setStage(STAGES.MENU);
    };

    const openStore = (store) => {
        sr.stop();
        setActiveStore(store);
        setStage(STAGES.STORE);
        const data = SAMPLE_STORE_RESULTS[store.id] || DEFAULT_STORE_RESULTS;
        const intro =
            data.kind === "restaurants"
                ? `I've opened ${store.name}. Here are the top restaurants near you.`
                : `I've opened ${store.name}. What would you like to find?`;
        setTimeout(() => speak(intro), 250);
    };

    const handleDemoCommand = (cmd) => {
        sr.stop();
        speak(`Got it. ${cmd.label}.`);
        openStore(cmd.store);
    };

    const handleItem = (item) => {
        setSelectedItem(item);
        const data = SAMPLE_STORE_RESULTS[activeStore?.id] || DEFAULT_STORE_RESULTS;
        speak(
            data.kind === "restaurants"
                ? `${item.name}. ${item.rating} stars. Delivery in ${item.meta}. Shall I open the menu, or would you like to keep looking?`
                : `${item.name}. ${item.price}. ${item.rating} stars. Shall I add it to your cart, or would you like to keep looking?`,
        );
        setStage(STAGES.SELECTED);
    };

    const addToCart = () => {
        speak(
            `Added to your cart. Your total is $21.98. Ready to check out?`,
        );
        setStage(STAGES.CART);
    };

    const checkout = () => {
        speak(
            "Starting secure checkout. Step 1 of 3: Please use Face ID or your fingerprint to confirm.",
        );
    };

    // STAGE: MENU
    if (stage === STAGES.MENU) {
        return (
            <div className="space-y-6">
                <button
                    type="button"
                    onClick={startListening}
                    data-testid="tell-clara-shop-btn"
                    className="w-full rounded-3xl bg-[#0A3D7A] hover:bg-[#082F5E] text-white p-5 flex items-center gap-4 shadow-lg transition-colors"
                >
                    <span
                        aria-hidden="true"
                        className="relative shrink-0 w-14 h-14 rounded-2xl bg-[#B8860B] flex items-center justify-center"
                    >
                        <span className="absolute inset-0 rounded-2xl bg-[#B8860B]/60 pulse-ring" />
                        <Mic className="relative w-7 h-7 text-[#1A1A2E]" strokeWidth={2.5} />
                    </span>
                    <span className="text-left">
                        <span className="block text-[11px] uppercase tracking-[0.2em] text-[#E0C875] font-semibold">
                            Speak naturally
                        </span>
                        <span className="block font-display text-xl font-bold leading-tight">
                            Tell Clara Where to Shop
                        </span>
                    </span>
                </button>

                {SHOPPING_CATEGORIES.map((cat) => (
                    <section key={cat.id} aria-label={cat.title}>
                        <h2 className="font-display text-xl font-bold text-[#1A1A2E] mb-3">
                            {cat.title}
                        </h2>
                        <div className="grid grid-cols-2 gap-3">
                            {cat.stores.map((s) => (
                                <button
                                    key={s.id}
                                    type="button"
                                    onClick={() => openStore(s)}
                                    aria-label={`Shop on ${s.name}`}
                                    data-testid={`store-card-${s.id}`}
                                    className="bg-white rounded-2xl p-4 min-h-[100px] flex items-center gap-3 border border-[#0A3D7A]/10 shadow-[0_2px_12px_rgba(10,61,122,0.06)] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_rgba(10,61,122,0.12)] transition-all text-left"
                                >
                                    <StoreMonogram name={s.name} category={cat.id} />
                                    <span className="font-semibold text-[#1A1A2E] leading-tight">
                                        {s.name}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </section>
                ))}

                <button
                    type="button"
                    onClick={() => openStore({ id: "other", name: "Custom website" })}
                    aria-label="Shop on any other website"
                    data-testid="any-other-website-btn"
                    className="w-full bg-transparent rounded-2xl p-5 min-h-[80px] flex items-center gap-3 border-2 border-dashed border-[#0A3D7A]/40 hover:border-[#0A3D7A] hover:bg-[#0A3D7A]/5 transition-colors text-left"
                >
                    <Globe
                        className="w-7 h-7 text-[#0A3D7A]"
                        aria-hidden="true"
                    />
                    <span>
                        <span className="block font-display font-bold text-[#1A1A2E]">
                            Any Other Website
                        </span>
                        <span className="block text-sm text-[#5A5A6E]">
                            Tell Clara the URL or store name
                        </span>
                    </span>
                </button>
            </div>
        );
    }

    // STAGE: LISTENING
    if (stage === STAGES.LISTENING) {
        return (
            <div className="space-y-4">
                <ClaraListening
                    title="Clara is listening"
                    subtitle={
                        sr.supported
                            ? "Speak naturally. Say what you'd like to shop for, and Clara will open the right store."
                            : "Voice recognition isn't available here. Tap a demo command below to continue."
                    }
                    examples={VOICE_EXAMPLES}
                    liveTranscript={sr.transcript}
                    isLive={sr.isListening}
                    onCancel={cancelListening}
                />
                <div className="rounded-3xl bg-white border border-[#0A3D7A]/10 p-5 shadow-sm">
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B8860B] mb-3">
                        Demo: simulate a voice command
                    </p>
                    <div className="space-y-2">
                        {DEMO_COMMANDS.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => handleDemoCommand(c)}
                                data-testid={`demo-command-${c.id}`}
                                className="w-full min-h-[52px] rounded-2xl border-2 border-[#0A3D7A] text-[#0A3D7A] font-semibold hover:bg-[#0A3D7A]/5 transition-colors px-4 flex items-center gap-2 text-left"
                            >
                                <Radio className="w-4 h-4 shrink-0" aria-hidden="true" />
                                <span>“{c.label}”</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // STAGE: STORE
    if (stage === STAGES.STORE && activeStore) {
        const data = SAMPLE_STORE_RESULTS[activeStore.id] || DEFAULT_STORE_RESULTS;
        return (
            <div className="space-y-5">
                <header
                    data-testid="store-header"
                    className="rounded-3xl bg-white border border-[#0A3D7A]/10 p-5 flex items-center gap-3 shadow-sm"
                >
                    <button
                        type="button"
                        onClick={() => setStage(STAGES.MENU)}
                        aria-label="Back to stores"
                        data-testid="back-to-stores-btn"
                        className="w-10 h-10 rounded-xl bg-[#FFF8F0] border border-[#F0EAD6] flex items-center justify-center text-[#0A3D7A] hover:bg-[#F0EAD6] transition-colors"
                    >
                        <ArrowLeft className="w-5 h-5" aria-hidden="true" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs uppercase tracking-wider font-semibold text-[#5A5A6E]">
                            Shopping at
                        </p>
                        <p className="font-display font-bold text-lg text-[#1A1A2E] truncate">
                            {activeStore.name}
                        </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#B8860B] text-[#1A1A2E] px-3 py-1.5 text-xs font-bold uppercase tracking-wider">
                        <span className="w-2 h-2 rounded-full bg-[#0A5C36] animate-pulse" />
                        Voice-Guided · Live
                    </span>
                </header>

                <div className="rounded-2xl bg-[#0A3D7A] text-white p-4 flex items-start gap-3">
                    <Volume2
                        className="w-5 h-5 mt-0.5 text-[#E0C875] shrink-0"
                        aria-hidden="true"
                    />
                    <p className="text-sm leading-snug">
                        “I've opened {activeStore.name}. What would you like to
                        find?”
                    </p>
                </div>

                <div className="rounded-2xl bg-white border border-[#0A3D7A]/10 p-3 flex items-center gap-2">
                    <Search
                        className="w-5 h-5 text-[#5A5A6E] shrink-0"
                        aria-hidden="true"
                    />
                    <div className="flex-1">
                        <VoiceInput
                            id="store-search"
                            value={searchQuery}
                            onChange={setSearchQuery}
                            fieldLabel={`search at ${activeStore.name}`}
                            placeholder="Search or use voice"
                            testId="store-search-input"
                            inputClassName="w-full bg-transparent border-0 outline-none text-base placeholder:text-[#5A5A6E] py-2 pr-12"
                            inputProps={{ "aria-label": `Search ${activeStore.name}` }}
                        />
                    </div>
                </div>

                <ul className="space-y-3" data-testid="store-results">
                    {data.items.map((item) => (
                        <li key={item.id}>
                            <button
                                type="button"
                                onClick={() => handleItem(item)}
                                data-testid={`store-item-${item.id}`}
                                aria-label={`${item.name}, ${item.price}, ${item.rating} stars`}
                                className="w-full text-left bg-white rounded-2xl p-4 border border-[#0A3D7A]/10 hover:border-[#0A3D7A]/30 hover:-translate-y-0.5 hover:shadow-md transition-all"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <p className="font-semibold text-[#1A1A2E] leading-tight">
                                        {item.name}
                                    </p>
                                    <p className="font-display font-bold text-[#0A3D7A] shrink-0">
                                        {item.price}
                                    </p>
                                </div>
                                <div className="mt-1.5 flex items-center gap-2 text-sm text-[#5A5A6E]">
                                    <span className="inline-flex items-center gap-1 text-[#B8860B] font-semibold">
                                        <Star
                                            className="w-4 h-4 fill-current"
                                            aria-hidden="true"
                                        />
                                        {item.rating}
                                    </span>
                                    <span aria-hidden="true">·</span>
                                    <span>{item.meta}</span>
                                </div>
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    // STAGE: SELECTED
    if (stage === STAGES.SELECTED && selectedItem) {
        return (
            <div className="space-y-5">
                <div className="rounded-2xl bg-[#0A3D7A] text-white p-4 flex items-start gap-3">
                    <Volume2
                        className="w-5 h-5 mt-0.5 text-[#E0C875] shrink-0"
                        aria-hidden="true"
                    />
                    <p className="text-sm leading-snug">
                        “Got it! Shall I add it to your cart, or would you like to
                        keep looking?”
                    </p>
                </div>

                <section
                    data-testid="selected-item-card"
                    className="bg-white rounded-3xl p-6 border-2 border-[#B8860B] shadow-[0_4px_20px_rgba(184,134,11,0.18)] fade-in-up"
                >
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B8860B] mb-1">
                        Selected
                    </p>
                    <h2 className="font-display text-2xl font-bold text-[#1A1A2E] leading-tight">
                        {selectedItem.name}
                    </h2>
                    <div className="mt-2 flex items-center gap-3">
                        <p className="font-display font-bold text-2xl text-[#0A3D7A]">
                            {selectedItem.price}
                        </p>
                        <span className="inline-flex items-center gap-1 text-[#B8860B] font-semibold">
                            <Star
                                className="w-4 h-4 fill-current"
                                aria-hidden="true"
                            />
                            {selectedItem.rating}
                        </span>
                        <span className="text-sm text-[#5A5A6E]">
                            · {selectedItem.meta}
                        </span>
                    </div>
                </section>

                <div className="space-y-3">
                    <button
                        type="button"
                        onClick={addToCart}
                        data-testid="add-to-cart-btn"
                        className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                    >
                        <Plus className="w-5 h-5" aria-hidden="true" />
                        Add to Cart
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            speak("Sure, keep browsing.");
                            setStage(STAGES.STORE);
                        }}
                        data-testid="keep-browsing-btn"
                        className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-lg font-bold transition-colors hover:bg-[#0A3D7A]/5"
                    >
                        Keep Browsing
                    </button>
                </div>
            </div>
        );
    }

    // STAGE: CART & CHECKOUT
    if (stage === STAGES.CART && selectedItem) {
        const itemTotal = selectedItem.price?.startsWith("$")
            ? selectedItem.price
            : "$18.99";
        return (
            <div className="space-y-5">
                <section
                    data-testid="cart-summary"
                    className="bg-white rounded-3xl overflow-hidden border border-[#0A3D7A]/10 shadow-[0_4px_20px_rgba(10,61,122,0.06)]"
                >
                    <div className="p-5 border-b border-[#F0EAD6] flex items-center gap-2">
                        <ShoppingCart
                            className="w-5 h-5 text-[#0A3D7A]"
                            aria-hidden="true"
                        />
                        <h2 className="font-display font-bold text-lg text-[#1A1A2E]">
                            Your cart · {activeStore?.name}
                        </h2>
                    </div>
                    <ul className="p-5 space-y-3">
                        <li className="flex items-start justify-between gap-3">
                            <p className="text-[#1A1A2E] font-semibold leading-tight">
                                {selectedItem.name}
                            </p>
                            <p className="font-display font-bold text-[#0A3D7A] shrink-0">
                                {itemTotal}
                            </p>
                        </li>
                        <li className="flex items-center justify-between text-sm text-[#5A5A6E]">
                            <span>Delivery</span>
                            <span>$2.99</span>
                        </li>
                        <li className="flex items-center justify-between text-sm text-[#5A5A6E]">
                            <span>Tax</span>
                            <span>$0.00</span>
                        </li>
                    </ul>
                    <div className="px-5 py-4 bg-[#FFF8F0] border-t border-[#F0EAD6] flex items-center justify-between">
                        <span className="font-semibold text-[#1A1A2E]">Total</span>
                        <span
                            className="font-display font-bold text-2xl text-[#0A3D7A]"
                            data-testid="cart-total"
                        >
                            $21.98
                        </span>
                    </div>
                </section>

                <div className="rounded-3xl bg-white border border-[#0A3D7A]/10 p-5">
                    <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B8860B] mb-3">
                        3-Layer Secure Checkout
                    </p>
                    <ol className="space-y-2.5">
                        <SecurityStep
                            Icon={Fingerprint}
                            label="Biometric — Face ID or fingerprint"
                            testId="secure-step-biometric"
                        />
                        <SecurityStep
                            Icon={KeyRound}
                            label="PIN — your 6-digit MyClearPath PIN"
                            testId="secure-step-pin"
                        />
                        <SecurityStep
                            Icon={Volume2}
                            label="Voice — say “confirm order”"
                            testId="secure-step-voice"
                        />
                    </ol>
                </div>

                <button
                    type="button"
                    onClick={checkout}
                    data-testid="voice-confirm-order-btn"
                    className="w-full min-h-[56px] rounded-2xl bg-[#0A5C36] hover:bg-[#0a4d2d] text-white text-lg font-bold flex items-center justify-center gap-2 shadow-sm transition-colors"
                >
                    <ShieldCheck className="w-5 h-5" aria-hidden="true" />
                    Voice-Confirm Order
                </button>
                <button
                    type="button"
                    onClick={() => setStage(STAGES.MENU)}
                    data-testid="back-to-shop-btn"
                    className="w-full min-h-[56px] rounded-2xl bg-white border-2 border-[#0A3D7A] text-[#0A3D7A] text-lg font-bold transition-colors hover:bg-[#0A3D7A]/5"
                >
                    Continue Shopping
                </button>
            </div>
        );
    }

    return null;
};

const SecurityStep = ({ Icon, label, testId }) => (
    <li
        className="flex items-center gap-3 p-3 rounded-2xl bg-[#FFF8F0] border border-[#F0EAD6]"
        data-testid={testId}
    >
        <span
            aria-hidden="true"
            className="shrink-0 w-9 h-9 rounded-lg bg-[#0A3D7A] flex items-center justify-center"
        >
            <Icon className="w-4 h-4 text-white" />
        </span>
        <p className="text-sm font-semibold text-[#1A1A2E] flex-1">{label}</p>
        <CheckCircle2
            className="w-5 h-5 text-[#0A5C36]"
            aria-hidden="true"
        />
    </li>
);

const STORE_COLORS = {
    doordash: { bg: "#FF3008", fg: "#FFFFFF" },
    ubereats: { bg: "#142328", fg: "#06C167" },
    grubhub: { bg: "#F63440", fg: "#FFFFFF" },
    postmates: { bg: "#000000", fg: "#FFE100" },
    instacart: { bg: "#0AAD0A", fg: "#FFFFFF" },
    walmart: { bg: "#0071CE", fg: "#FFC220" },
    amazonfresh: { bg: "#1A8917", fg: "#FFFFFF" },
    wholefoods: { bg: "#003F2D", fg: "#FFFFFF" },
    amazon: { bg: "#131A22", fg: "#FF9900" },
    target: { bg: "#CC0000", fg: "#FFFFFF" },
    etsy: { bg: "#F1641E", fg: "#FFFFFF" },
    wayfair: { bg: "#7B189F", fg: "#FFFFFF" },
    bestbuy: { bg: "#0046BE", fg: "#FFE000" },
    walgreens: { bg: "#E31837", fg: "#FFFFFF" },
};

const CATEGORY_ICONS = {
    food: Utensils,
    grocery: ShoppingBasket,
    stores: StoreIcon,
};

function StoreMonogram({ name, category }) {
    const id = name.toLowerCase().replace(/[^a-z]/g, "").slice(0, 12);
    const palette =
        STORE_COLORS[id] ||
        // try matching by removing suffix
        STORE_COLORS[Object.keys(STORE_COLORS).find((k) => id.startsWith(k)) || ""] ||
        { bg: "#0A3D7A", fg: "#FFE7A8" };
    const Icon = CATEGORY_ICONS[category] || StoreIcon;
    const initials = name
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    return (
        <div
            aria-hidden="true"
            className="relative shrink-0 w-12 h-12 rounded-xl flex items-center justify-center font-display font-bold text-base overflow-hidden"
            style={{ backgroundColor: palette.bg, color: palette.fg }}
        >
            <Icon
                className="absolute -bottom-1 -right-1 w-5 h-5 opacity-25"
                strokeWidth={2}
            />
            <span className="relative tracking-tight">{initials}</span>
        </div>
    );
}

export default VoiceShopping;
